/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, Fragment, useMemo, useCallback, useEffect } from "react";

// antd:
import { Steps } from "antd";
// import { LoadingOutlined } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

// My Components:
import AppIcons from "../react/AppIcons.tsx";
import DivE from "../react/DivE.tsx";
// import SpanE from "../react/SpanE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getTheme, getLng, getDict, getUser, getAppState, changeAppState, getSafePtr } from "../../libs/reducer.ts";
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

const { rz } = window;

type Tsteps = {
  type:"default" | "navigation" | "inline",
  direction:"horizontal" | "vertical",
  labelPlacement:"horizontal" | "vertical",
  size:"default" | "small"
  progressDot:boolean,
  responsive:boolean,
};

type Tval = {
  index:number,
  key:string,
  status:"wait" | "process" | "finish" | "error"
};

const RZsteps:FC<TuniversalElementProps> = ({attributes}) => { // children
  RZsteps.displayName = "Steps";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const theme = useAppSelector(getTheme);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, content} = attributes; // stepstype, value (current), direction
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath];
  const {color, backgroundColor} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const stepsType = useMemo<Tsteps>(() => {
    const typ:Tsteps = {type: "default", direction: "horizontal", labelPlacement: "horizontal", size: "default", progressDot: false, responsive: false};
    (type || "").toLowerCase().replace((/(\s+|,|\|)/igm), ";").split(";").forEach((tt) => {
      if (~["horizontal", "vertical"].indexOf(tt)) { typ.direction = tt }
      if (~["horizontallabel", "verticallabel"].indexOf(tt)) { typ.labelPlacement = tt.replace("label", "") }
      if (~["small", "default"].indexOf(tt)) { typ.size = tt }
      if (~["dot", "progressdot"].indexOf(tt)) { typ.progressDot = true }
      if (tt === "responsive") { typ.responsive = true }
    });
    return typ;
  }, [type]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    // if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass]);

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    return est;
  }, [elState?.userStyle, elState?.visible, color, backgroundColor, theme, permissions]);

  const activeStepChange = useCallback((activeStepItem) => {
    const stepKey = content[activeStepItem].key;
    if (fnc.config?.isDev) console.log({action: "<Steps key='" + key + "'> -> activeStepChange('" + stepKey + "')"});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: stepKey}}));
    if (rootNamespace) {
      const clientFNC = rootNamespace[attributes?.onChange || attributes?.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, stepKey, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret?.err) { console.log(ret.err) }
          if (ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, stepKey, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret?.err) { console.log(ret.err) }
        if (ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [content, key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const val = useMemo<Tval>(() => {
    const vl:Tval = {index: 0, status: "wait", key: ""};
    const vs = (elState?.value || "").toLowerCase().trim().replace((/\s+/igm), "|").split("|");
    const va = (attributes?.value || "").toLowerCase().trim().replace((/\s+/igm), "|").split("|");
    if (vs[0]) { vl.key = vs[0]; if (vs[1] || elState?.status) { vl.status = vs[1] || elState?.status } }
    else if (va[0]) { vl.key = va[0]; if (va[1]) { vl.status = va[1] } }
    if (vl.key) { for (let i = 0; i < content.length; i++) { if (content[i].key === vl.key) { vl.index = i; break } } }
    else if (content[0]?.key) { vl.key = content[0].key }
    return vl;
  }, [elState?.value, elState?.status, attributes?.value, content]);

  const hasContent = useMemo(() => {
    let hasCnt = false;
    for (let i = 0; i < content.length; i++) { if (content[i].content && content[i].content.length) { hasCnt = true; break } }
    return hasCnt;
  }, [content]);

  if (hasContent) {
    return (
      <DivE key={key + "-stepscontainer"} id={key + "-stepscontainer"} >
        <Steps
          key={key}
          type={attributes?.stepstype || stepsType.type || "navigation" /* || "inline" || "default" */}
          size={attributes?.size || stepsType.size || "default" /* || "small" */}
          responsive={stepsType.responsive}
          direction={attributes?.direction || stepsType.direction || "horizontal" /* || "vertical" */}
          labelPlacement={((attributes?.progressDot === "true" || stepsType.progressDot) ? "vertical" : (attributes?.labelPlacement || stepsType.labelPlacement || "vertical" /* || "horizontal" */))}
          progressDot={attributes?.progressDot === "true" || stepsType.progressDot} // labelPlacement will be vertical
          status={val.status} current={val.index} // stateValue:string = "selectedKey|status" -> selectedIndex & status
          onChange={activeStepChange}
          items={content.map((tt) => ({
            key: tt.key, id: tt.key,
            icon: (tt.attributes?.icon ? (<AppIcons key={tt.key + "-ico"} icopath={tt.attributes.icon} attrs={{ color: tt.attributes?.iconColor || "black|white", className: tt.attributes?.iconClass || "" }} />) : undefined),
            title: (tt.attributes["title_" + lng] || tt.attributes.title || tt.attributes["title_" + rlng]),
            subTitle: (tt.attributes["subtitle_" + lng] || tt.attributes.subtitle || tt.attributes["subtitle_" + rlng]),
            description: (tt.attributes["description_" + lng] || tt.attributes.description || tt.attributes["description_" + rlng]),
            status: (appState[tt.fullPath]?.status || tt.attributes?.status),
            disabled: (appState[tt.fullPath]?.disabled || tt.attributes?.disabled === true || tt.attributes?.disabled === "true"),
            hidden: (appState[tt.fullPath].visible === false)
          }))}
        />
          <DivE key={key + "-allsteps"} id={key + "-allsteps"} className={clsu.join(" ")} style={styl} uk-tooltip={tooltip} >
            {content.map((ss) => {
              const cntState = appState[ss.fullPath], cntStyle = {...cntState.userStyle}, cntClass = rz.objClone(cntState.userClass || []);
              const cntPermissions = fnc.checkPermissions(user, {...ss?.attributes, rootAttributes: {key: rootAttributes.key}});
              // Style
              if ((val.key !== ss.key || cntPermissions.visible === false) && cntStyle.display !== "none") { cntStyle.display = "none" }
              else if ((val.key === ss.key && cntPermissions.visible !== false) && cntStyle.display === "none") { cntStyle.display = undefined }
              // className
              if (!~cntClass.indexOf("uk-container-expand")) { cntClass.push("uk-container-expand") }
              if (attributes?.align || ss.attributes?.align) {
                if (!~cntClass.indexOf("uk-flex")) { cntClass.push("uk-flex") }
                (attributes?.align || ss.attributes?.align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt:string) => {
                  if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt) && !~cntClass.indexOf("uk-flex-" + tt)) {
                    cntClass.push("uk-flex-" + tt);
                  }
                });
              }
              if ((attributes?.borders || ss.attributes?.borders) && !rz.arrContains(cntClass, /^rz-borders-/igm)) {
                (attributes?.borders || ss.attributes?.borders || "").split(" ").filter((ff) => ((/^(light|dark|l1|l2|l3|l4|l5|d1|d2|d3|d4|d5|hover)$/igm).test(ff)))
                  .forEach((ff:string) => { if (!~cntClass.indexOf("rz-borders-" + ff)) { cntClass.push("rz-borders-" + ff) } });
              }

              return (
                <DivE key={ss.key + "-step-content"} id={ss.key + "-step-content"} className={cntClass.join(" ")} style={cntStyle}>
                  {(ss.content || []).map((el) => (el.children))}
                </DivE>
              )
            })}
          </DivE>
      </DivE>
    );
  }

  return (
    <Steps className={clsu.join(" ")} style={styl} uk-tooltip={tooltip}
      key={key}
      type={attributes?.stepstype || stepsType.type} // "navigation" || "inline" || "default"
      size={attributes?.size || stepsType.size} // "default" || "small"
      responsive={stepsType.responsive}
      direction={attributes?.direction || stepsType.direction} // "horizontal" || "vertical"
      labelPlacement={((attributes?.progressDot === "true" || stepsType.progressDot) ? "vertical" : (attributes?.labelPlacement || stepsType.labelPlacement || "vertical"))}
      progressDot={attributes?.progressDot === "true" || stepsType.progressDot} // labelPlacement will be vertical
      status={val.status} current={val.index} // stateValue:string = "selectedKey|status" -> selectedIndex & status
      onChange={activeStepChange}
      items={content.map((tt) => ({
        key: tt.key,
        icon: (tt.attributes?.icon ? (<AppIcons key={tt.key + "-ico"} icopath={tt.attributes.icon} attrs={{ color: tt.attributes?.iconColor || "black|white", className: tt.attributes?.iconClass || "" }} />) : undefined),
        title: (tt.attributes["title_" + lng] || tt.attributes.title || tt.attributes["title_" + rlng]),
        subTitle: (tt.attributes["subtitle_" + lng] || tt.attributes.subtitle || tt.attributes["subtitle_" + rlng]),
        description: (tt.attributes["description_" + lng] || tt.attributes.description || tt.attributes["description_" + rlng]),
        status: (appState[tt.fullPath]?.value || tt.attributes?.status), // value:string -> status
        disabled: (appState[tt.fullPath]?.disabled || tt.attributes?.disabled === true || tt.attributes?.disabled === "true"),
        hidden: (appState[tt.fullPath].visible === false)
      }))}
    />
  );
}

export default RZsteps;
/* eslint-enable @typescript-eslint/no-unused-vars */
